import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Building2, Briefcase, Users } from "lucide-react";

const STATUS_TONE = {
    active: "bg-emerald-50 text-emerald-800 border-emerald-200",
    paused: "bg-amber-50 text-amber-800 border-amber-200",
    archived: "bg-zinc-100 text-zinc-700 border-zinc-300",
};

export default function ClientsPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/clients");
            setItems(data?.items || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const remove = async (c) => {
        if (!window.confirm(`Delete client "${c.name}"? Linked jobs will be unassigned.`)) return;
        try {
            await api.delete(`/clients/${c.id}`);
            toast.success("Client deleted");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <><div className="max-w-[1600px] mx-auto p-3 sm:p-5 lg:p-6 space-y-6">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-xs uppercase tracking-widest font-mono text-ikb">Clients</div>
                        <h1 className="font-display font-black text-2xl sm:text-4xl mt-2">Client accounts</h1>
                        <p className="text-sm text-muted-foreground mt-2">Companies you're recruiting for. Assign jobs and candidates to clients to track 3rd-party billing & performance.</p>
                    </div>
                    <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="client-new-btn">
                        <Plus className="w-4 h-4 mr-2" /> New client
                    </Button>
                </div>

                {loading ? (
                    <div className="text-sm font-mono text-foreground/70">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="border border-dashed border-border p-16 text-center">
                        <Building2 className="w-8 h-8 mx-auto text-foreground/50" />
                        <h3 className="font-display font-bold text-xl mt-4">No clients yet</h3>
                        <p className="text-sm text-muted-foreground mt-1">Add the first client you're recruiting for.</p>
                    </div>
                ) : (
                    <div className="border border-border bg-white divide-y divide-border" data-testid="clients-list">
                        {items.map((c) => (
                            <div key={c.id} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`client-row-${c.id}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="font-display font-bold text-base truncate">{c.name}</h3>
                                        <span className={`text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 ${STATUS_TONE[c.status] || STATUS_TONE.active}`}>{c.status}</span>
                                        {c.industry && <span className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-2 py-0.5">{c.industry}</span>}
                                    </div>
                                    <div className="text-xs text-foreground/70 mt-1 font-mono">
                                        {c.contact_name && `${c.contact_name}`}{c.contact_email && ` · ${c.contact_email}`}{c.contact_phone && ` · ${c.contact_phone}`}
                                    </div>
                                </div>
                                <div className="flex items-center gap-4 text-xs font-mono uppercase tracking-widest text-foreground/70">
                                    <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> {c.jobs_count || 0} jobs</span>
                                    <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {c.candidates_count || 0} candidates</span>
                                </div>
                                <div className="flex gap-1">
                                    <button onClick={() => { setEditing(c); setOpen(true); }} className="p-2 border border-border hover:bg-secondary" data-testid={`client-edit-${c.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                                    <button onClick={() => remove(c)} className="p-2 border border-border hover:bg-rose-50 hover:text-rose-700" data-testid={`client-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <ClientFormDialog open={open} onClose={() => setOpen(false)} client={editing} onSaved={load} />
        </>);
}

function ClientFormDialog({ open, onClose, client, onSaved }) {
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setForm(client || { status: "active" });
    }, [client, open]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async () => {
        if (!form.name?.trim()) return toast.error("Name is required");
        setBusy(true);
        try {
            if (client) await api.patch(`/clients/${client.id}`, form);
            else await api.post("/clients", form);
            toast.success(client ? "Client updated" : "Client created");
            onSaved();
            onClose();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-none max-w-lg" data-testid="client-dialog">
                <DialogHeader><DialogTitle>{client ? "Edit client" : "New client"}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <Field label="Name *">
                        <Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} className="rounded-none h-10" data-testid="client-name" />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Contact name">
                            <Input value={form.contact_name || ""} onChange={(e) => set("contact_name", e.target.value)} className="rounded-none h-10" data-testid="client-contact-name" />
                        </Field>
                        <Field label="Industry">
                            <Input value={form.industry || ""} onChange={(e) => set("industry", e.target.value)} className="rounded-none h-10" />
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Email">
                            <Input value={form.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className="rounded-none h-10" />
                        </Field>
                        <Field label="Phone">
                            <Input value={form.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className="rounded-none h-10" />
                        </Field>
                    </div>
                    <Field label="Website">
                        <Input value={form.website || ""} onChange={(e) => set("website", e.target.value)} className="rounded-none h-10" />
                    </Field>
                    <Field label="Status">
                        <Select value={form.status || "active"} onValueChange={(v) => set("status", v)}>
                            <SelectTrigger className="rounded-none h-10" data-testid="client-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {["active", "paused", "archived"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Notes">
                        <textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={3} className="w-full border border-border p-2 text-sm rounded-none" />
                    </Field>
                </div>
                <DialogFooter>
                    <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="client-save">
                        {busy ? "Saving…" : client ? "Save changes" : "Create client →"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/80 mb-1 block">{label}</Label>
            {children}
        </div>
    );
}
